import { WHITE } from "../tokens";
import { ElementType, ReactNode, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { EditablePath, useEditable } from './EditableContext'
import { TEXT_VALUE } from '../tokens'

interface Props {
  // Where this value lives inside the report payload. When the user commits
  // an edit, the provider's onChange is called with this exact path and the
  // new string value.
  path: EditablePath
  value: string
  // Component / element used in read mode. Defaults to a fragment-like span
  // so EditableText can stand in anywhere a plain string would.
  as?: ElementType
  // Multi-line content (essence.content, observations) renders in a textarea
  // when editing. Single-line uses an input.
  multiline?: boolean
  placeholder?: string
  // Optional renderer for the display value (e.g. to resolve {{file:uuid}} tokens).
  // When provided, replaces the raw string in all non-editing views.
  renderValue?: (v: string) => ReactNode
}

const EditableWrapper = styled.span<{ $editable: boolean }>`
  ${(p) =>
    p.$editable &&
    `
    cursor: text;
    /* Visible at rest, not only on hover: otherwise there is nothing telling
       the editor where one field ends and the next begins. */
    border-bottom: 1px dashed rgba(99, 102, 241, 0.35);
    transition: border-color 0.15s ease, background-color 0.15s ease;
    &:hover {
      border-bottom-color: #6366f1;
      background-color: rgba(99, 102, 241, 0.06);
    }
  `}
`

// Both fields force their own background, so they force their own ink too:
// inheriting it picks up whatever the host app set (admin-interface's index.css
// still carries Vite's near-white default), which on white is invisible.
const EDITING_INK = TEXT_VALUE

const EditingInput = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  padding: 0.4rem 0.5rem;
  border: 1px solid #6366f1;
  border-radius: 0.4rem;
  background: ${WHITE};
  font: inherit;
  color: ${EDITING_INK};
  resize: vertical;
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
`

const EditingSingleLine = styled.input`
  width: 100%;
  padding: 0.3rem 0.5rem;
  border: 1px solid #6366f1;
  border-radius: 0.4rem;
  background: ${WHITE};
  font: inherit;
  color: ${EDITING_INK};
  outline: none;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
`

export const EditableText = ({
  path,
  value,
  as,
  multiline = false,
  placeholder,
  renderValue,
}: Props) => {
  const { editable, onChange } = useEditable()
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null)

  // Keep the local draft in sync when the upstream value changes (e.g.
  // another field's edit triggered a re-render with a fresh report object).
  useEffect(() => {
    if (!isEditing) setDraft(value)
  }, [value, isEditing])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const displayValue = renderValue ? renderValue(value) : (value || null)

  if (!editable) {
    const Tag = (as || 'span') as ElementType
    return <Tag>{displayValue}</Tag>
  }

  const commit = () => {
    setIsEditing(false)
    if (draft !== value) onChange(path, draft)
  }

  const cancel = () => {
    setDraft(value)
    setIsEditing(false)
  }

  if (isEditing) {
    if (multiline) {
      return (
        <EditingInput
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Escape') cancel()
            // Cmd/Ctrl+Enter commits; plain Enter is allowed for newlines.
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) commit()
          }}
        />
      )
    }
    return (
      <EditingSingleLine
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel()
          if (e.key === 'Enter') commit()
        }}
      />
    )
  }

  const Tag = (as || 'span') as ElementType
  return (
    <EditableWrapper
      as={Tag}
      $editable
      onClick={(e: React.MouseEvent) => {
        e.stopPropagation()
        setIsEditing(true)
      }}
      title="Click para editar"
    >
      {displayValue || (placeholder ? <em style={{ opacity: 0.5 }}>{placeholder}</em> : null)}
    </EditableWrapper>
  )
}
